"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  CheckResults,
  type PythCtx,
  SolveResults,
} from "@/components/tools/linear-algebra/pythagorean-results";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  classifyTriangle,
  solveRightTriangle,
  type Unknown,
} from "@/lib/tool-kit/linear-algebra/right-triangle";
import { decimalMarkOf } from "@/lib/tool-kit/math/locale";
import {
  type NumberStyle,
  parseRational,
  type Rational,
} from "@/lib/tool-kit/math/rational";

/** English strings. A translated content file overrides these through `ui`. */
export const PYTHAGOREAN_THEOREM_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to do?",
  modes: {
    solve: "Find the missing side",
    check: "Check whether three sides make a right triangle",
  },
  unknownLabel: "Side to find",
  unknowns: {
    c: "Hypotenuse c",
    a: "Leg a",
    b: "Leg b",
  },
  sideInputs: {
    a: "Leg a",
    b: "Leg b",
    c: "Hypotenuse c",
  },
  sideN: "Side {n}",
  lengthHint: "Positive whole numbers, decimals or fractions such as 3/2.",
  results: "Results",
  enterAll: "Enter every side length to see the result.",
  invalid:
    "Enter each length as a positive number or a fraction, such as 3, 2.5 or 7/2.",
  notPositive: "Side lengths must be greater than zero.",
  legTooLong:
    "The hypotenuse is always the longest side. Make c larger than the leg, or the leg smaller than c.",
  solvedLabel: "Side {side}",
  areaLabel: "Area",
  perimeterLabel: "Perimeter",
  copied: "Copied!",
  copyResult: "Copy result",
  workingHeading: "The working",
  theorem: "a² + b² = c²",
  workingHypotenuse: "c² = {a}² + {b}² = {a2} + {b2} = {sum}",
  workingLeg: "{side}² = c² − {other}² = {c}² − {o}² = {c2} − {o2} = {diff}",
  workingRoot: "{side} = √{radicand} = {root}",
  workingRootSimple: "{side} = √{radicand} ≈ {decimal}",
  noteSimplified: "√{n} = √({square} × {rad}) = {coef}√{rad}",
  noteSimplest:
    "{n} has no square factor, so √{n} is already in simplest radical form.",
  triplePrimitive:
    "All three sides are whole numbers with no common factor, so {triple} is a primitive Pythagorean triple.",
  tripleInteger:
    "All three sides are whole numbers, so this is a Pythagorean triple: the primitive triple {triple} multiplied by {factor}.",
  tripleScaled:
    "These sides are in the ratio of the Pythagorean triple {triple}: each is that triple multiplied by {factor}.",
  notTriple:
    "At least one side is irrational, so these sides are not a Pythagorean triple (a triple needs three whole numbers).",
  diagramHeading: "Diagram",
  diagramTitle:
    "A right triangle drawn to scale with the right angle at the lower left, leg a along the bottom, leg b on the left and hypotenuse c opposite the right angle",
  downloadPng: "Download diagram (PNG)",
  notToScale:
    "One leg is far shorter than the other, so it is drawn at a minimum length: this drawing is not to scale.",
  verdictLabel: "The triangle is",
  kinds: {
    right: "Right",
    acute: "Acute",
    obtuse: "Obtuse",
  },
  sumSquaresLabel: "a² + b²",
  longestSquaredLabel: "c² (longest side)",
  checkWorking:
    "Sides in order: {a}, {b}, {c}. a² + b² = {sum} and c² = {longest}, so a² + b² {relation} c².",
  kindExplain: {
    right:
      "a² + b² = c², so by the converse of the Pythagorean theorem the angle opposite the longest side is exactly 90°.",
    acute:
      "a² + b² > c², so the angle opposite the longest side is less than 90°, and all three angles are acute.",
    obtuse:
      "a² + b² < c², so the angle opposite the longest side is greater than 90°.",
  },
  noTriangle:
    "Sides {a}, {b} and {c} cannot form a triangle: the two shorter sides must add up to more than the longest.",
} as const;

export type PythagoreanStrings = typeof PYTHAGOREAN_THEOREM_CALCULATOR_STRINGS;

type Mode = keyof PythagoreanStrings["modes"];

const DECIMAL_INPUT = /[.,]/;
const MODE_ORDER: Mode[] = ["solve", "check"];
const UNKNOWN_ORDER: Unknown[] = ["c", "a", "b"];

function Outcome({
  mode,
  unknown,
  sides,
  checkSides,
  ctx,
}: {
  mode: Mode;
  unknown: Unknown;
  sides: Record<Unknown, string>;
  checkSides: string[];
  ctx: PythCtx;
}) {
  const { strings } = ctx;
  const texts =
    mode === "solve"
      ? (["a", "b", "c"] as const)
          .filter((n) => n !== unknown)
          .map((n) => sides[n])
      : checkSides;
  if (texts.some((text) => text.trim() === "")) {
    return <Notice>{strings.enterAll}</Notice>;
  }
  const values = texts.map(parseRational);
  if (values.some((x) => x === null)) {
    return <ErrorNotice>{strings.invalid}</ErrorNotice>;
  }
  const [first, second, third] = values as Rational[];
  if (mode === "solve") {
    const result = solveRightTriangle(unknown, first, second);
    if (result === "not-positive") {
      return <ErrorNotice>{strings.notPositive}</ErrorNotice>;
    }
    if (result === "leg-too-long") {
      return <ErrorNotice>{strings.legTooLong}</ErrorNotice>;
    }
    return <SolveResults ctx={ctx} result={result} />;
  }
  const result = classifyTriangle([first, second, third]);
  if (result === "not-positive") {
    return <ErrorNotice>{strings.notPositive}</ErrorNotice>;
  }
  return <CheckResults ctx={ctx} result={result} />;
}

export default function PythagoreanTheoremCalculatorWidget() {
  const strings = useToolUi(PYTHAGOREAN_THEOREM_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("solve");
  const [unknown, setUnknown] = useState<Unknown>("c");
  const [sides, setSides] = useState<Record<Unknown, string>>({
    a: "3",
    b: "4",
    c: "5",
  });
  const [checkSides, setCheckSides] = useState(["6", "8", "10"]);
  const shown =
    mode === "solve"
      ? (["a", "b", "c"] as const)
          .filter((n) => n !== unknown)
          .map((n) => sides[n])
      : checkSides;
  const style: NumberStyle = shown.some((text) => DECIMAL_INPUT.test(text))
    ? "decimal"
    : "fraction";
  const ctx: PythCtx = { strings, style, mark: decimalMarkOf(fmt), fmt };
  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="pt-mode"
        onChange={setMode}
        options={MODE_ORDER.map((id) => ({
          value: id,
          label: strings.modes[id],
        }))}
        value={mode}
      />
      {mode === "solve" ? (
        <>
          <PillGroup
            label={strings.unknownLabel}
            name="pt-unknown"
            onChange={setUnknown}
            options={UNKNOWN_ORDER.map((id) => ({
              value: id,
              label: strings.unknowns[id],
            }))}
            value={unknown}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            {(["a", "b", "c"] as const)
              .filter((n) => n !== unknown)
              .map((n) => (
                <TextField
                  id={`pt-${n}`}
                  key={n}
                  label={strings.sideInputs[n]}
                  mono
                  numeric
                  onChange={(value) =>
                    setSides((prev) => ({ ...prev, [n]: value }))
                  }
                  value={sides[n]}
                />
              ))}
          </div>
        </>
      ) : (
        <div className="grid gap-4 sm:grid-cols-3">
          {checkSides.map((text, i) => (
            <TextField
              id={`pt-check-${i}`}
              // biome-ignore lint/suspicious/noArrayIndexKey: three fixed positional fields
              key={i}
              label={t(strings.sideN, { n: fmt(i + 1) })}
              mono
              numeric
              onChange={(value) =>
                setCheckSides((prev) =>
                  prev.map((x, j) => (j === i ? value : x))
                )
              }
              value={text}
            />
          ))}
        </div>
      )}
      <p className="text-muted-foreground text-xs">{strings.lengthHint}</p>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome
          checkSides={checkSides}
          ctx={ctx}
          mode={mode}
          sides={sides}
          unknown={unknown}
        />
      </section>
    </>
  );
}
